import React, { useState } from 'react';
import { GoalTypeConfirmationProps, GOAL_TYPE_DESCRIPTIONS } from './types';
import { GoalType } from '../Milestone_organizer/types';

/**
 * GoalTypeConfirmation - Stap 3 van de intake flow
 * 
 * Toont de gesuggereerde goal type met beschrijving.
 * Gebruiker kan:
 * - Suggestie bevestigen
 * - Zelf een ander type kiezen
 * 
 * De gebruiker bevestigt ALTIJD zelf het type.
 */

const GOAL_TYPES: GoalType[] = ['A', 'B', 'C'];

const GoalTypeConfirmation: React.FC<GoalTypeConfirmationProps> = ({
  suggestedType,
  onConfirm,
  onBack
}) => {
  const [selectedType, setSelectedType] = useState<GoalType>(suggestedType);

  const handleConfirm = () => {
    onConfirm(selectedType);
  };

  return (
    <div className="max-w-xl mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 mb-2">
          Welk type doel is dit?
        </h2>
        <p className="text-gray-600">
          Op basis van je antwoorden denken we dat dit een{' '}
          <span className="font-semibold text-orange-600">
            {GOAL_TYPE_DESCRIPTIONS[suggestedType].title.split(':')[0]}
          </span>{' '}
          is. Klopt dit?
        </p>
      </div>

      {/* Goal type options */}
      <div className="space-y-4 mb-8">
        {GOAL_TYPES.map((type) => {
          const { title, description } = GOAL_TYPE_DESCRIPTIONS[type];
          const isSelected = selectedType === type;
          const isSuggested = suggestedType === type;

          return (
            <button
              key={type}
              onClick={() => setSelectedType(type)}
              className={`w-full p-5 text-left rounded-lg border-2 transition-all ${
                isSelected
                  ? 'border-orange-500 bg-orange-50'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-gray-900">{title}</span>
                    {isSuggested && (
                      <span className="text-xs px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full">
                        Suggestie
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600">{description}</p>
                </div>
                <div className={`ml-4 w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                  isSelected 
                    ? 'border-orange-500 bg-orange-500' 
                    : 'border-gray-300'
                }`}>
                  {isSelected && (
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Buttons */}
      <div className="flex justify-between items-center pt-4">
        <button
          onClick={onBack}
          className="px-4 py-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          &larr; Terug
        </button>
        <button
          onClick={handleConfirm}
          className="px-6 py-3 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 active:bg-orange-700 transition-colors"
        >
          Bevestig {selectedType}-type
        </button>
      </div>
    </div>
  );
};

export default GoalTypeConfirmation;
